import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate, Navigate } from 'react-router';
import { useMsal } from '@azure/msal-react';
import queryString from 'query-string';
import { Button, Form, InputPassword } from '../../components/ui';
import renderNotification from '../../helpers/notifications/renderNotification';
import { DEFAULT_RULES } from '../../consts/formRules';
import { loginRequest, INITIAL_SIGNIN_REDIRECT_URI } from '../../config/msal';
import { ALLOWED_SESSION_TYPES } from '../../consts/consts';
import { selectActivePrivacyStatementUrl, selectActiveTermsOfUseUrl } from '../../redux/config/selectors';
import {
  selectCurrentUser,
  selectIsLoggedIn,
  selectLoginWithPasswordAndApproveTermsOfUseLoading,
  selectLoginWithPasswordAndApproveTermsOfUseFailed,
  selectLoginWithMicrosoftAndApproveTermsOfUseLoading,
  selectAllowedSessionTypes,
  selectAllowedSessionTypesLoading
} from '../../redux/authentication/selectors';
import {
  loginWithPasswordAndApproveTermsOfUse,
  logout,
  getAllowedSessionTypes
} from '../../redux/authentication/slice';
import { IconMicrosoft } from '../../components/ui/Icons';
import PasswordRules from './PasswordRules';
import {
  LoginContentWrapper,
  LoginSectionWrapper,
  LoginWelcomMessage,
  LoginFormBackground,
  LoginDivider,
  LoginDividerText,
  FormDescriptionTextWrapper,
  InitialSigninEmailWrapper,
  InitialSigninEmailLabel,
  UserAgreementMessageWrapper
} from './Login.style';

/**
 * Functional component for the initial sign-in process.
 * Allows users to set their password and accept currentUser agreements.
 */
const InitialSignIn = ({
  activePrivacyStatementUrl,
  activeTermsOfUseUrl,
  isLoggedIn,
  loginWithPasswordAndApproveTermsOfUse,
  loginWithPasswordAndApproveTermsOfUseFailed,
  loginWithPasswordAndApproveTermsOfUseLoading,
  loginWithMicrosoftAndApproveTermsOfUseLoading,
  allowedSessionTypes,
  allowedSessionTypesLoading,
  getAllowedSessionTypes
}) => {
  const [password, setPassword] = useState('');
  const [form] = Form.useForm();
  const { validateFields } = form;
  const location = useLocation();
  const { from } = location.state || { from: { pathname: '/' } };
  const navigate = useNavigate();
  const { instance } = useMsal();
  const email = queryString.parse(location.search).email || null;
  const token = queryString.parse(location.search).token || null;

  const isLoginSuccess =
    !loginWithPasswordAndApproveTermsOfUseLoading && isLoggedIn && !loginWithPasswordAndApproveTermsOfUseFailed;

  useEffect(() => {
    if (email) {
      getAllowedSessionTypes({ currentUserEmail: email });
    }
  }, [email]);

  // Handle form submission and trigger sign-in process
  const handleSubmit = () => {
    validateFields()
      .then((values) => {
        if (
          loginWithPasswordAndApproveTermsOfUseLoading ||
          loginWithMicrosoftAndApproveTermsOfUseLoading ||
          allowedSessionTypesLoading
        ) {
          return;
        }
        const { password } = values;
        if (password) {
          loginWithPasswordAndApproveTermsOfUse({ token, username: email, password, navigate });
        }
      })
      .catch(() => {});
  };

  const handleFormValuesChange = (changedValues) => {
    setPassword(changedValues.password);
  };

  // Handle Microsoft SSO login
  const handleMicrosoftLogin = async () => {
    try {
      await instance.loginRedirect({ ...loginRequest, redirectUri: INITIAL_SIGNIN_REDIRECT_URI });
    } catch {
      renderNotification({ type: 'error', message: 'Microsoft-Anmeldung fehlgeschlagen' });
    }
  };

  // Render the currentUser agreement message with links to terms and privacy policy
  const renderUserAgreementMessage = () => (
    <>
      {'Mit der Anmeldung akzeptieren Sie die '}
      <a href={activeTermsOfUseUrl} target={'_blank'} rel="noopener noreferrer">
        Nutzungsbedingungen
      </a>
      {' und die '}
      <a href={activePrivacyStatementUrl} target={'_blank'} rel="noopener noreferrer">
        Datenschutzerklärung
      </a>
      {' des Portals. '}
    </>
  );

  const isPasswordValid = (password) => {
    const minLength = password && password.length >= 12;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    return minLength && hasUpperCase && hasLowerCase && hasNumber;
  };

  const isPasswordLoginAllowed = allowedSessionTypes?.includes(ALLOWED_SESSION_TYPES.PASSWORD);
  const isMicrosoftLoginAllowed = allowedSessionTypes?.includes(ALLOWED_SESSION_TYPES.MICROSOFT);

  const renderSigninFormContent = () => {
    if (isPasswordLoginAllowed && !isMicrosoftLoginAllowed) {
      return (
        <LoginSectionWrapper>
          <FormDescriptionTextWrapper>Vergeben Sie ein Passwort für Ihren Account.</FormDescriptionTextWrapper>
          <InitialSigninEmailLabel>E-Mail</InitialSigninEmailLabel>
          <InitialSigninEmailWrapper>{email || '—.—@oqio.de'}</InitialSigninEmailWrapper>
          <Form form={form} onValuesChange={handleFormValuesChange} requiredMark="optional">
            <Form.Item
              name="password"
              label="Passwort"
              className="initial-signin-password-input"
              rules={DEFAULT_RULES.INPUT_REQUIRED}
            >
              <InputPassword status={loginWithPasswordAndApproveTermsOfUseFailed ? 'error' : ''} />
            </Form.Item>

            <PasswordRules password={password} />
            <UserAgreementMessageWrapper>{renderUserAgreementMessage()}</UserAgreementMessageWrapper>
          </Form>
          <Button type="primary" onClick={handleSubmit} block disabled={!isPasswordValid(password)}>
            Speichern
          </Button>
        </LoginSectionWrapper>
      );
    }

    if (!isPasswordLoginAllowed && isMicrosoftLoginAllowed) {
      return (
        <LoginSectionWrapper>
          <FormDescriptionTextWrapper>Melden Sie sich mit Microsoft an.</FormDescriptionTextWrapper>
          <UserAgreementMessageWrapper>{renderUserAgreementMessage()}</UserAgreementMessageWrapper>
          <Button
            type="secondary"
            block
            onClick={handleMicrosoftLogin}
            loading={loginWithMicrosoftAndApproveTermsOfUseLoading}
            icon={<IconMicrosoft />}
          >
            Mit Microsoft anmelden
          </Button>
        </LoginSectionWrapper>
      );
    }

    return (
      <>
        <LoginSectionWrapper>
          <FormDescriptionTextWrapper>
            Vergeben Sie ein Passwort für Ihren Account oder melden sich mit Microsoft an.
          </FormDescriptionTextWrapper>
          <InitialSigninEmailLabel>E-Mail</InitialSigninEmailLabel>
          <InitialSigninEmailWrapper>{email || '—.—@oqio.de'}</InitialSigninEmailWrapper>
          <Form form={form} onValuesChange={handleFormValuesChange} requiredMark="optional">
            <Form.Item
              name="password"
              label="Passwort"
              className="initial-signin-password-input"
              rules={DEFAULT_RULES.INPUT_REQUIRED}
            >
              <InputPassword status={loginWithPasswordAndApproveTermsOfUseFailed ? 'error' : ''} />
            </Form.Item>

            <PasswordRules password={password} />
            <UserAgreementMessageWrapper>{renderUserAgreementMessage()}</UserAgreementMessageWrapper>
          </Form>
          <Button type="primary" onClick={handleSubmit} block disabled={!isPasswordValid(password)}>
            Speichern
          </Button>
        </LoginSectionWrapper>
        <LoginDivider>
          <LoginDividerText>oder</LoginDividerText>
        </LoginDivider>
        <LoginSectionWrapper>
          <Button
            type="secondary"
            block
            onClick={handleMicrosoftLogin}
            loading={loginWithMicrosoftAndApproveTermsOfUseLoading}
            icon={<IconMicrosoft />}
          >
            Mit Microsoft anmelden
          </Button>
        </LoginSectionWrapper>
      </>
    );
  };

  return isLoginSuccess ? (
    <Navigate to={from} replace />
  ) : (
    <LoginContentWrapper>
      <LoginWelcomMessage>Willkommen zu Ihrem Versicherungsportal.</LoginWelcomMessage>
      <LoginFormBackground>{renderSigninFormContent()}</LoginFormBackground>
    </LoginContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  loginWithPasswordAndApproveTermsOfUseLoading: selectLoginWithPasswordAndApproveTermsOfUseLoading(state),
  loginWithMicrosoftAndApproveTermsOfUseLoading: selectLoginWithMicrosoftAndApproveTermsOfUseLoading(state),
  currentUser: selectCurrentUser(state),
  isLoggedIn: selectIsLoggedIn(state),
  loginWithPasswordAndApproveTermsOfUseFailed: selectLoginWithPasswordAndApproveTermsOfUseFailed(state),
  activePrivacyStatementUrl: selectActivePrivacyStatementUrl(state),
  activeTermsOfUseUrl: selectActiveTermsOfUseUrl(state),
  allowedSessionTypes: selectAllowedSessionTypes(state),
  allowedSessionTypesLoading: selectAllowedSessionTypesLoading(state)
});

const mapDispatchToProps = {
  loginWithPasswordAndApproveTermsOfUse,
  logout,
  getAllowedSessionTypes
};

export default connect(mapStateToProps, mapDispatchToProps)(InitialSignIn);
